// client/src/components/common/NotificationBell.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  ShoppingCart,
  Package,
  FileText,
  MessageCircle,
  XCircle,
  Shield,
  CheckCircle,
  Bell,
  AlertCircle,
  Truck,
  Tag 
} from 'lucide-react';

const NotificationBell = () => {
  const { t } = useTranslation('common');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  
  // ✅ FIX 1: Get isAuthenticated to check login status
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    // ✅ FIX 2: Only start polling if user is logged in
    if (isAuthenticated) {
      fetchNotifications();
      
      const interval = setInterval(() => {
        fetchUnreadCount();
      }, 60000);
      
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]); // Re-run when login status changes

  const fetchNotifications = async () => {
    // ✅ FIX 3: Stop immediately if not logged in (Prevents 401 error)
    if (!isAuthenticated) return;

    setLoading(true);
    try {
      const response = await notificationsAPI.getAll();
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unreadCount);
    } catch (err) {
      // Ignore 401 errors to keep console clean
      if (err.response && err.response.status !== 401) {
        console.error('Failed to fetch notifications:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    if (!isAuthenticated) return;

    try {
      const response = await notificationsAPI.getUnreadCount();
      setUnreadCount(response.data.unreadCount);
    } catch (err) {
      if (err.response && err.response.status !== 401) {
        console.error('Failed to fetch unread count:', err);
      }
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.read) {
      try {
        await notificationsAPI.markAsRead(notification._id);
        setNotifications(notifications.map(n => 
          n._id === notification._id ? { ...n, read: true } : n
        ));
        setUnreadCount(Math.max(0, unreadCount - 1));
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
      }
    }

    // Navigate based on notification type / associated entity
    setShowDropdown(false);

    if (notification.type === 'concierge_batch_complete') {
      navigate('/wholesaler/catalog-requests#approved');
      return;
    }

    if (notification.orderId) {
      if (user?.role === 'wholesaler') {
        navigate('/wholesaler/orders');
      } else if (user?.role === 'business') {
        navigate('/my-orders');
      } else {
        navigate('/orders');
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleClearRead = async () => {
    try {
      await notificationsAPI.clearRead();
      setNotifications(notifications.filter(n => !n.read));
    } catch (err) {
      console.error('Failed to clear read notifications:', err);
    }
  };

  const getNotificationIcon = (type) => {
    const iconProps = { size: 20, className: "flex-shrink-0" };
    
    switch (type) {
      case 'new_order':
        return <div className="p-2 bg-blue-100 rounded-full"><ShoppingCart {...iconProps} className="text-blue-600" /></div>;
      case 'order_status':
        return <div className="p-2 bg-green-100 rounded-full"><Package {...iconProps} className="text-green-600" /></div>;
      case 'order_shipped':
        return <div className="p-2 bg-purple-100 rounded-full"><Truck {...iconProps} className="text-purple-600" /></div>;
      case 'order_modified':
        return <div className="p-2 bg-amber-100 rounded-full"><AlertCircle {...iconProps} className="text-amber-600" /></div>;
      case 'invoice_uploaded':
        return <div className="p-2 bg-purple-100 rounded-full"><FileText {...iconProps} className="text-purple-600" /></div>;
      case 'order_comment':
        return <div className="p-2 bg-cyan-100 rounded-full"><MessageCircle {...iconProps} className="text-cyan-600" /></div>;
      case 'flash_offer':
        return <div className="p-2 bg-red-100 rounded-full"><Tag {...iconProps} className="text-red-600" /></div>;
      case 'order_cancelled':
        return <div className="p-2 bg-red-100 rounded-full"><XCircle {...iconProps} className="text-red-600" /></div>;
      case 'security_alert':
        return <div className="p-2 bg-orange-100 rounded-full"><Shield {...iconProps} className="text-orange-600" /></div>;
      default:
        return <div className="p-2 bg-gray-100 rounded-full"><Bell {...iconProps} className="text-gray-600" /></div>;
    }
  };

  // ✅ FIX 4: Don't render ANYTHING if not logged in
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => {
          setShowDropdown(!showDropdown);
          if (!showDropdown) {
            fetchNotifications();
          }
        }}
        className="relative p-2 text-gray-700 hover:text-blue-600 transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setShowDropdown(false)}
          />
          
          <div className="fixed sm:absolute left-4 right-4 sm:left-auto sm:right-0 mt-2 sm:w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-20 max-h-[32rem] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="font-semibold text-gray-800">{t('notifications.title')}</h3>
              <div className="flex space-x-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    {t('notifications.markAllRead')}
                  </button>
                )}
                {notifications.some(n => n.read) && (
                  <button
                    onClick={handleClearRead}
                    className="text-xs text-gray-600 hover:text-gray-700"
                  >
                    {t('notifications.clearRead')}
                  </button>
                )}
              </div>
            </div>

            <div className="overflow-y-auto flex-1">
              {loading ? (
                <div className="p-8 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2">{t('notifications.loading')}</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Bell className="w-16 h-16 mx-auto mb-2 text-gray-300" />
                  <p>{t('notifications.empty')}</p>
                </div>
              ) : (
                <div className="divide-y">
                  {notifications.map((notification) => (
                    <div
                      key={notification._id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                        !notification.read ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        {getNotificationIcon(notification.type)}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${!notification.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                            {notification.title}
                          </p>
                          <p className="text-sm text-gray-600 mt-1 break-words">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                          </p>
                        </div>
                        {!notification.read && (
                          <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-2"></div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;